import React, { useState, useEffect } from 'react';
import { FaPiggyBank, FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaSpinner, FaCheck, FaCalendarAlt } from 'react-icons/fa';
import { useNotification } from '@/contexts/NotificationContext';
import { carryoverService } from '@/services/carryover.service';
import type { AnnualCarryover, CurrencyBalances } from '@/types/carryover';


interface FormData {
  year: number;
  sourceYear: number;
  HUF: number;
  EUR: number;
  USD: number;
  KRW: number;
  GBP: number;
  CAD: number;
  notes: string;
}

const initialFormData: FormData = {
  year: new Date().getFullYear(),
  sourceYear: new Date().getFullYear() - 1,
  HUF: 0,
  EUR: 0,
  USD: 0,
  KRW: 0,
  GBP: 0,
  CAD: 0,
  notes: ''
};

// 🌟 연간 이월금 관리 컴포넌트 (관리자 전용)
export const CarryoverFundsManager: React.FC = () => {
  const { addNotification } = useNotification();
  const [carryovers, setCarryovers] = useState<AnnualCarryover[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);

  useEffect(() => {
    loadCarryovers();
  }, []);

  const loadCarryovers = async () => {
    setLoading(true);
    try {
      const data = await carryoverService.getAllAnnualCarryovers();
      setCarryovers(data);
    } catch (error) {
      console.error('이월금 데이터 로딩 오류:', error);
      addNotification('이월금 데이터를 불러올 수 없습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formDataToBalances = (): CurrencyBalances => ({
    HUF: formData.HUF || 0,
    EUR: formData.EUR || 0,
    USD: formData.USD || 0,
    KRW: formData.KRW || 0,
    GBP: formData.GBP || 0,
    CAD: formData.CAD || 0,
  });

  const balancesToFormData = (balances: CurrencyBalances, carryover: AnnualCarryover) => {
    setFormData({
      year: carryover.year,
      sourceYear: carryover.sourceYear,
      HUF: balances.HUF || 0,
      EUR: balances.EUR || 0,
      USD: balances.USD || 0,
      KRW: balances.KRW || 0,
      GBP: balances.GBP || 0,
      CAD: balances.CAD || 0,
      notes: carryover.notes || ''
    });
  };

  const handleAdd = async () => {
    const balances = formDataToBalances();
    if (Object.values(balances).every(amount => amount === 0)) {
      addNotification('최소 하나의 통화에 금액을 입력해주세요.', 'error');
      return;
    }

    setActionLoading('add');
    try {
      await carryoverService.createAnnualCarryover({
        year: formData.year,
        sourceYear: formData.sourceYear,
        balances: balances,
        notes: formData.notes || `${formData.sourceYear}년 말 잉여금 이월`
      });

      addNotification('이월금이 성공적으로 추가되었습니다.', 'success');
      await loadCarryovers();
      setShowAddForm(false);
      setFormData(initialFormData);
    } catch (error: any) {
      console.error('이월금 추가 오류:', error);
      addNotification(error.message || '이월금 추가 중 오류가 발생했습니다.', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleEdit = (carryover: AnnualCarryover) => {
    setEditingId(carryover.id);
    balancesToFormData(carryover.balances, carryover);
  };

  const handleUpdate = async (id: string) => {
    setActionLoading(id);
    try {
      await carryoverService.updateAnnualCarryover(id, {
        year: formData.year,
        sourceYear: formData.sourceYear,
        balances: formDataToBalances(),
        notes: formData.notes
      });

      addNotification('이월금이 성공적으로 수정되었습니다.', 'success');
      await loadCarryovers();
      setEditingId(null);
    } catch (error: any) {
      console.error('이월금 수정 오류:', error);
      addNotification(error.message || '이월금 수정 중 오류가 발생했습니다.', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (id: string, year: number) => {
    if (!confirm(`${year}년 이월금을 삭제하시겠습니까?`)) {
      return;
    }

    setActionLoading(id);
    try {
      await carryoverService.deleteAnnualCarryover(id);
      addNotification('이월금이 성공적으로 삭제되었습니다.', 'success');
      await loadCarryovers();
    } catch (error: any) {
      console.error('이월금 삭제 오류:', error);
      addNotification(error.message || '이월금 삭제 중 오류가 발생했습니다.', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleConfirm = async (id: string) => {
    if (!confirm('이 이월금을 확정하시겠습니까? 확정 후에는 수정이 제한됩니다.')) {
      return;
    }

    setActionLoading(id);
    try {
      await carryoverService.confirmAnnualCarryover(id);
      addNotification('이월금이 확정되었습니다.', 'success');
      await loadCarryovers();
    } catch (error: any) {
      console.error('이월금 확정 오류:', error);
      addNotification(error.message || '이월금 확정 중 오류가 발생했습니다.', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const formatAmount = (amount: number, currency: string): string => {
    const symbols: Record<string, string> = { HUF: 'Ft', EUR: '€', USD: '$', KRW: '₩', GBP: '£', CAD: 'CA$' };
    return `${amount.toLocaleString()} ${symbols[currency] || currency}`;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden h-full flex flex-col">
      {/* 헤더 */}
      <div className="px-6 py-5 bg-gradient-to-r from-green-600 to-emerald-600">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <FaPiggyBank className="h-8 w-8 text-white mr-3" />
            <div>
              <h3 className="text-xl font-bold text-white">연간 이월금 관리</h3>
              <p className="text-green-100 text-sm">전년도 잔액을 새 연도로 이월합니다</p>
            </div>
          </div>

          <button
            onClick={() => setShowAddForm(true)}
            className="inline-flex items-center px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors backdrop-blur-sm"
          >
            <FaPlus className="h-4 w-4 mr-2" />
            이월금 추가
          </button>
        </div>
      </div>

      {/* 새 이월금 추가 폼 */}
      {showAddForm && (
        <div className="px-6 py-5 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-b">
          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <FaCalendarAlt className="mr-2 text-blue-500" />
            새 연간 이월금 추가
          </h4>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                적용 연도
              </label>
              <input
                type="number"
                value={formData.year}
                onChange={(e) => setFormData(prev => ({ ...prev, year: parseInt(e.target.value) || 0 }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                출처 연도
              </label>
              <input
                type="number"
                value={formData.sourceYear}
                onChange={(e) => setFormData(prev => ({ ...prev, sourceYear: parseInt(e.target.value) || 0 }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                HUF (포린트)
              </label>
              <input
                type="number"
                value={formData.HUF}
                onChange={(e) => setFormData(prev => ({ ...prev, HUF: parseFloat(e.target.value) || 0 }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                EUR (유로)
              </label>
              <input
                type="number"
                value={formData.EUR}
                onChange={(e) => setFormData(prev => ({ ...prev, EUR: parseFloat(e.target.value) || 0 }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                USD (달러)
              </label>
              <input
                type="number"
                value={formData.USD}
                onChange={(e) => setFormData(prev => ({ ...prev, USD: parseFloat(e.target.value) || 0 }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                KRW (원)
              </label>
              <input
                type="number"
                value={formData.KRW}
                onChange={(e) => setFormData(prev => ({ ...prev, KRW: parseFloat(e.target.value) || 0 }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">GBP (파운드)</label>
              <input type="number" value={formData.GBP} onChange={(e) => setFormData(prev => ({ ...prev, GBP: parseFloat(e.target.value) || 0 }))} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" placeholder="0" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CAD (캐나다 달러)</label>
              <input type="number" value={formData.CAD} onChange={(e) => setFormData(prev => ({ ...prev, CAD: parseFloat(e.target.value) || 0 }))} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" placeholder="0" />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              비고 (선택)
            </label>
            <input
              type="text"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="예: 2024년 말 잉여금 이월"
            />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              onClick={() => { setShowAddForm(false); setFormData(initialFormData); }}
              className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <FaTimes className="inline mr-2" />
              취소
            </button>
            <button
              onClick={handleAdd}
              disabled={actionLoading === 'add'}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition-colors"
            >
              {actionLoading === 'add' ? (
                <FaSpinner className="inline animate-spin mr-2" />
              ) : (
                <FaSave className="inline mr-2" />
              )}
              저장
            </button>
          </div>
        </div>
      )}

      {/* 콘텐츠 */}
      <div className="p-6 flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <FaSpinner className="h-8 w-8 animate-spin text-green-500" />
            <span className="ml-3 text-gray-600 dark:text-gray-400">로딩 중...</span>
          </div>
        ) : carryovers.length === 0 ? (
          <div className="text-center py-12">
            <FaPiggyBank className="h-16 w-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
            <p className="text-gray-500 dark:text-gray-400 text-lg">등록된 이월금이 없습니다.</p>
            <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">
              위의 "이월금 추가" 버튼을 클릭해서 시작하세요.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {carryovers.map((carryover) => (
              <div
                key={carryover.id}
                className={`border rounded-xl p-5 transition-all ${carryover.isConfirmed
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700'
                  : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:shadow-md'
                  }`}
              >
                {editingId === carryover.id ? (
                  // 수정 모드
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">적용 연도</label>
                        <input
                          type="number"
                          value={formData.year}
                          onChange={(e) => setFormData(prev => ({ ...prev, year: parseInt(e.target.value) || 0 }))}
                          className="w-full px-2 py-1 text-sm border rounded"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">HUF</label>
                        <input
                          type="number"
                          value={formData.HUF}
                          onChange={(e) => setFormData(prev => ({ ...prev, HUF: parseFloat(e.target.value) || 0 }))}
                          className="w-full px-2 py-1 text-sm border rounded"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">EUR</label>
                        <input
                          type="number"
                          value={formData.EUR}
                          onChange={(e) => setFormData(prev => ({ ...prev, EUR: parseFloat(e.target.value) || 0 }))}
                          className="w-full px-2 py-1 text-sm border rounded"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">USD</label>
                        <input
                          type="number"
                          value={formData.USD}
                          onChange={(e) => setFormData(prev => ({ ...prev, USD: parseFloat(e.target.value) || 0 }))}
                          className="w-full px-2 py-1 text-sm border rounded"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">KRW</label>
                        <input type="number" value={formData.KRW} onChange={(e) => setFormData(prev => ({ ...prev, KRW: parseFloat(e.target.value) || 0 }))} className="w-full px-2 py-1 text-sm border rounded" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">GBP</label>
                        <input type="number" value={formData.GBP} onChange={(e) => setFormData(prev => ({ ...prev, GBP: parseFloat(e.target.value) || 0 }))} className="w-full px-2 py-1 text-sm border rounded" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">CAD</label>
                        <input type="number" value={formData.CAD} onChange={(e) => setFormData(prev => ({ ...prev, CAD: parseFloat(e.target.value) || 0 }))} className="w-full px-2 py-1 text-sm border rounded" />
                      </div>
                    </div>
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => setEditingId(null)}
                        className="px-3 py-1 text-gray-600 hover:bg-gray-100 rounded"
                      >
                        취소
                      </button>
                      <button
                        onClick={() => handleUpdate(carryover.id)}
                        disabled={actionLoading === carryover.id}
                        className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        {actionLoading === carryover.id ? <FaSpinner className="animate-spin" /> : '저장'}
                      </button>
                    </div>
                  </div>
                ) : (
                  // 보기 모드
                  <div className="flex flex-col">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <span className="text-2xl font-bold text-gray-900 dark:text-white">
                          {carryover.year}년
                        </span>
                        <span className="ml-3 text-sm text-gray-500">
                          ← {carryover.sourceYear}년에서 이월
                        </span>
                        {carryover.isConfirmed && (
                          <span className="ml-3 px-2 py-1 bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-100 text-xs rounded-full flex items-center">
                            <FaCheck className="mr-1" /> 확정됨
                          </span>
                        )}
                      </div>

                      {/* 액션 버튼 (헤더 우측 배치) */}
                      <div className="flex items-center space-x-1">
                        {!carryover.isConfirmed && (
                          <button
                            onClick={() => handleConfirm(carryover.id)}
                            disabled={actionLoading === carryover.id}
                            className="p-2 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30 rounded-lg transition-colors"
                            title="확정하기"
                          >
                            <FaCheck className="h-4 w-4" />
                          </button>
                        )}

                        <button
                          onClick={() => handleEdit(carryover)}
                          className="p-2 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                          title="수정하기"
                        >
                          <FaEdit className="h-4 w-4" />
                        </button>

                        <button
                          onClick={() => handleDelete(carryover.id, carryover.year)}
                          disabled={actionLoading === carryover.id}
                          className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                          title="삭제하기"
                        >
                          {actionLoading === carryover.id ? (
                            <FaSpinner className="h-4 w-4 animate-spin" />
                          ) : (
                            <FaTrash className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-1">
                      {carryover.balances.HUF > 0 && (
                        <div className="bg-gray-100 dark:bg-gray-700 rounded-lg px-3 py-2">
                          <div className="text-xs text-gray-500 dark:text-gray-400">HUF</div>
                          <div className="font-semibold text-gray-900 dark:text-white">
                            {formatAmount(carryover.balances.HUF, 'HUF')}
                          </div>
                        </div>
                      )}
                      {carryover.balances.EUR > 0 && (
                        <div className="bg-blue-100 dark:bg-blue-900/30 rounded-lg px-3 py-2">
                          <div className="text-xs text-blue-600 dark:text-blue-400">EUR</div>
                          <div className="font-semibold text-blue-800 dark:text-blue-200">
                            {formatAmount(carryover.balances.EUR, 'EUR')}
                          </div>
                        </div>
                      )}
                      {carryover.balances.USD > 0 && (
                        <div className="bg-green-100 dark:bg-green-900/30 rounded-lg px-3 py-2">
                          <div className="text-xs text-green-600 dark:text-green-400">USD</div>
                          <div className="font-semibold text-green-800 dark:text-green-200">
                            {formatAmount(carryover.balances.USD, 'USD')}
                          </div>
                        </div>
                      )}
                      {carryover.balances.KRW > 0 && (
                        <div className="bg-purple-100 dark:bg-purple-900/30 rounded-lg px-3 py-2">
                          <div className="text-xs text-purple-600 dark:text-purple-400">KRW</div>
                          <div className="font-semibold text-purple-800 dark:text-purple-200">
                            {formatAmount(carryover.balances.KRW, 'KRW')}
                          </div>
                        </div>
                      )}
                      {(carryover.balances.GBP || 0) > 0 && <div className="bg-gray-100 dark:bg-gray-700 rounded-lg px-3 py-2"><div className="text-xs text-gray-500 dark:text-gray-400">GBP</div><div className="font-semibold text-gray-900 dark:text-white">{formatAmount(carryover.balances.GBP, 'GBP')}</div></div>}
                      {(carryover.balances.CAD || 0) > 0 && <div className="bg-gray-100 dark:bg-gray-700 rounded-lg px-3 py-2"><div className="text-xs text-gray-500 dark:text-gray-400">CAD</div><div className="font-semibold text-gray-900 dark:text-white">{formatAmount(carryover.balances.CAD, 'CAD')}</div></div>}
                    </div>

                    {carryover.notes && (
                      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                        📝 {carryover.notes}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
